import { SHOWROOM_ADDRESS_LINE, showroomMapSearchUrl } from './showroom-location.js';

export type StorefrontLandingExperiencePathIcon = 'shop' | 'showroom' | 'consultation' | 'tour';

export type StorefrontLandingExperiencePath = {
  id: string;
  icon: StorefrontLandingExperiencePathIcon;
  title: string;
  href: string;
  externalShop?: boolean;
  cta: string;
};

export type StorefrontLandingFaqItem = {
  question: string;
  answer: string;
};

export type StorefrontLandingEditorialCard = {
  title: string;
  body?: string;
  href: string;
  imageUrl?: string;
};

export type StorefrontLandingPromotionFallback = {
  title: string;
  pct?: string;
  to: string;
  desc?: string;
};

export type StorefrontLandingNavSection =
  | 'accueil'
  | 'showroom'
  | 'marques'
  | 'conseil'
  | 'univers'
  | 'besoins'
  | 'bestSellers'
  | 'offres'
  | 'guides'
  | 'faq';

export type StorefrontLandingNavItem = {
  section: StorefrontLandingNavSection;
  label: string;
};

export type StorefrontLandingUniversFallbackTile = {
  productType?: string;
  href?: string;
  label: string;
  featured?: boolean;
  imageUrl?: string;
};

export type StorefrontHomeLandingContent = {
  meta: {
    title: string;
    description: string;
  };
  hero: {
    displayTitle: string;
    accent: string;
    eyebrow?: string;
    lead?: string;
    trustLine?: string;
    trustChips?: string[];
    trustChipsAria?: string;
    ctaPrimary: string;
    ctaPrimaryHref: string;
    ctaGhost: string;
    ctaGhostHref: string;
  };
  experience: {
    title: string;
    paths: StorefrontLandingExperiencePath[];
  };
  showroom: {
    title: string;
    accent: string;
    addressLine: string;
    hours: string;
    phone: string;
    /** Full-bleed landing band (parallel to diagnostic). */
    bandTitle: string;
    ctaVisit: string;
    ctaVisitHref: string;
    ctaTour: string;
    ctaTourHref: string;
    ctaDirections: string;
    mapHref: string;
    tourEyebrow: string;
    tourTitle: string;
    tourLead: string;
  };
  consultation: {
    title: string;
    lead?: string;
    inPersonTitle: string;
    inPersonCta: string;
    inPersonHref: string;
    onlineTitle: string;
    onlineCta: string;
    onlineHref: string;
    onlineUnavailable: string;
  };
  brandStrip: {
    ariaLabel: string;
  };
  univers: {
    title: string;
    featuredLabel: string;
    carouselPrev: string;
    carouselNext: string;
    fallbackTiles: StorefrontLandingUniversFallbackTile[];
  };
  besoins: {
    title: string;
    viewAllCta: string;
    viewAllHref: string;
  };
  bestSellers: {
    title: string;
    viewAllCta: string;
  };
  offers: {
    title: string;
    viewAllCta: string;
    fallbackCards: StorefrontLandingPromotionFallback[];
  };
  editorial: {
    title: string;
    lead?: string;
    readGuideCta: string;
    readGuideHref: string;
    besoinsCta: string;
    besoinsHref: string;
    carouselPrev: string;
    carouselNext: string;
    cards: StorefrontLandingEditorialCard[];
  };
  faq: {
    title: string;
    items: StorefrontLandingFaqItem[];
  };
  closing: {
    title: string;
    ctaPrimary: string;
    ctaPrimaryHref: string;
    ctaSecondary: string;
    ctaSecondaryHref: string;
    ctaOnline: string;
    ctaOnlineHref: string;
  };
  nav: StorefrontLandingNavItem[];
  footer: {
    aboutBlurb: string;
  };
};

export const STOREFRONT_HOME_LANDING_KEY = 'storefront.home.landing';

const SHOWROOM_COPY_RE = /showroom/i;
const SHOWROOM_HREF_RE = /section-showroom|showroom-3d|#showroom/i;
const RETIRED_VISIT_COPY_RE = /showroom|une visite|en boutique|avant de venir/i;
/** Old CTAs that name the tool instead of the outcome, or retired shop labels. */
const RETIRED_DIAGNOSTIC_CTA_RE =
  /^(diagnostic (ia|gratuit)(\s*[—–-]\s*3\s*min)?|obtenir ma routine(\s*\(3\s*min\))?|lancer (le diagnostic|l['’]analyse)|analyse en ligne|faire mon diagnostic peau\s*[—–-]\s*3\s*min)$/i;
const RETIRED_SHOP_CTA_RE = /^(choisir mon soin|boutique en ligne)$/i;
/** Retired hero eyebrow / lead (label + bridge copy removed from first viewport). */
const RETIRED_HERO_EYEBROW_RE = /pharmaciens partenaires|livraison partout/i;
const RETIRED_HERO_LEAD_RE =
  /parapharmacie et cosmétique|marques authentiques|diagnostic peau,? puis boutique|livraison 24\s*[–-]\s*48h et paiement|paiement à la livraison/i;
const VERBOSE_HERO_TRUST_LINE_RE = /livraison|retour|paiement|800\s*mad/i;
const HERO_LOGISTICS_CHIP_RE = /livraison|800\s*mad|paiement|retour|authentique/i;

function mentionsShowroom(value: string | undefined): boolean {
  return value != null && SHOWROOM_COPY_RE.test(value);
}

function isRetiredVisitCopy(value: string | undefined): boolean {
  return value != null && RETIRED_VISIT_COPY_RE.test(value);
}

function isRetiredDiagnosticCta(value: string | undefined): boolean {
  return value != null && RETIRED_DIAGNOSTIC_CTA_RE.test(value.trim());
}

function isRetiredShopCta(value: string | undefined): boolean {
  return value != null && RETIRED_SHOP_CTA_RE.test(value.trim());
}

function isRetiredHeroLead(value: string | undefined): boolean {
  return value != null && value.trim() !== '' && RETIRED_HERO_LEAD_RE.test(value);
}

function isRetiredHeroEyebrow(value: string | undefined): boolean {
  return value != null && value.trim() !== '' && RETIRED_HERO_EYEBROW_RE.test(value);
}

function isVerboseHeroTrustLine(value: string | undefined): boolean {
  return value != null && value.trim() !== '' && VERBOSE_HERO_TRUST_LINE_RE.test(value);
}

function sanitizeHeroTrustChips(chips: string[] | undefined, fallback: string[]): string[] {
  if (!chips?.length) return fallback;
  // Hero no longer carries logistics proof; strip known chips so CMS cannot reintroduce them.
  if (chips.some((c) => HERO_LOGISTICS_CHIP_RE.test(c.trim()))) return fallback;
  return chips;
}

/** Rewrite CMS hrefs that still point at the retired landing showroom block. */
export function landingHrefWithoutShowroom(href: string, fallback: string): string {
  const v = href.trim();
  if (!v || SHOWROOM_HREF_RE.test(v) || SHOWROOM_COPY_RE.test(v)) return fallback;
  return v;
}

export const DEFAULT_STOREFRONT_HOME_LANDING: StorefrontHomeLandingContent = {
  meta: {
    title: 'Conseil peau et boutique',
    description:
      'Parapharmacie et cosmétique au Maroc. Diagnostic peau pour une routine adaptée, puis boutique en ligne avec livraison.',
  },
  hero: {
    eyebrow: '',
    displayTitle: 'Votre beauté mérite des soins',
    accent: 'authentiques',
    lead: '',
    trustLine: '',
    trustChips: [],
    trustChipsAria: 'Réassurance',
    ctaPrimary: 'Faire mon diagnostic peau',
    ctaPrimaryHref: '/diagnostic',
    ctaGhost: 'Voir la boutique',
    ctaGhostHref: '/catalogue',
  },
  experience: {
    title: 'Parcours',
    paths: [
      {
        id: 'shop',
        icon: 'shop',
        title: 'Boutique en ligne',
        cta: 'Voir la boutique',
        href: '/catalogue',
        externalShop: true,
      },
      {
        id: 'consultation',
        icon: 'consultation',
        title: 'Consultation peau',
        cta: 'Faire mon diagnostic peau',
        href: '/diagnostic',
      },
    ],
  },
  showroom: {
    title: 'Venez essayer',
    accent: 'en boutique',
    addressLine: SHOWROOM_ADDRESS_LINE,
    hours: 'Lun–Sam · 10h–20h',
    phone: '+212 5XX-XXXXXX',
    bandTitle: 'Consultation dermo-cosmétique personnalisée — en showroom.',
    ctaVisit: 'Réserver ma place',
    ctaVisitHref: 'tel:+2125XXXXXXXX',
    ctaTour: 'Visite 3D',
    ctaTourHref: '/#showroom-3d',
    ctaDirections: 'Voir sur la carte',
    mapHref: showroomMapSearchUrl(),
    tourEyebrow: 'Visite interactive',
    tourTitle: 'HAMSSAH 3D Showroom Tour',
    tourLead: 'Parcourez le concept store zone par zone, à votre rythme.',
  },
  consultation: {
    title: 'Votre peau est unique. Votre routine devrait l’être aussi.',
    lead: 'Diagnostic en ligne : partez avec une routine adaptée avant d’acheter.',
    inPersonTitle: 'Le concept Hamssah',
    inPersonCta: 'Comprendre Hamssah',
    inPersonHref: '/concept',
    onlineTitle: 'Diagnostic peau',
    onlineCta: 'Faire mon diagnostic peau',
    onlineHref: '/diagnostic',
    onlineUnavailable: 'Analyse en ligne bientôt disponible.',
  },
  brandStrip: {
    ariaLabel: 'Marques partenaires',
  },
  univers: {
    title: 'Par univers',
    featuredLabel: 'À la une',
    carouselPrev: 'Mise en avant précédente',
    carouselNext: 'Mise en avant suivante',
    fallbackTiles: [
      { productType: 'COSMETIC', label: 'Cosmétique' },
      { productType: 'PARAPHARMACEUTICAL', label: 'Parapharmacie' },
      { productType: 'PHARMACEUTICAL', label: 'Pharmacie' },
      { productType: 'DEVICE', label: 'Dispositifs' },
      { productType: 'OTHER', label: 'Autre' },
      { href: '/selection', label: 'À la une', featured: true },
    ],
  },
  besoins: {
    title: 'Par besoin',
    viewAllCta: 'Tout voir',
    viewAllHref: '/besoins',
  },
  bestSellers: {
    title: 'Meilleures ventes',
    viewAllCta: 'Voir tout le catalogue',
  },
  offers: {
    title: 'Offres du moment',
    viewAllCta: 'Voir toutes les promotions',
    /** Empty by default — section renders only when live campaigns exist. */
    fallbackCards: [],
  },
  editorial: {
    title: 'Conseils d’experts',
    lead: 'Guides courts et sélections du moment pour comprendre votre besoin avant d’acheter.',
    readGuideCta: 'Lire le guide',
    readGuideHref: '/guide',
    besoinsCta: 'Parcourir par besoin',
    besoinsHref: '/besoins',
    carouselPrev: 'Mise en avant précédente',
    carouselNext: 'Mise en avant suivante',
    cards: [
      {
        title: 'Routine visage',
        body: 'Nettoyage, préparation, soin : peu de gestes, des repères clairs.',
        href: '/guide/routine-visage',
      },
      {
        title: 'Soins ciblés',
        body: 'Formules pour peaux sensibles et besoins du moment.',
        href: '/guide/soins-cibles',
      },
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Comment obtenir un conseil peau ?',
        answer:
          'Faites le diagnostic peau en ligne (environ 3 minutes) pour une routine indicative matin / soir, ou lisez les guides Conseils. Une question ? Le bouton ? en bas à droite ouvre aussi la FAQ.',
      },
      {
        question: 'Quels sont les délais et frais de livraison ?',
        answer:
          'Livraison offerte dès 800 MAD. Délai indicatif 24–48h (standard partout au Maroc ; express Casa–Rabat lorsque proposé). Frais exacts confirmés au panier selon votre zone.',
      },
      {
        question: 'Puis-je payer à la livraison ?',
        answer:
          'Oui : paiement à la livraison (espèces ou carte selon le livreur), carte bancaire en ligne, ou virement lorsque ces modes sont actifs au checkout.',
      },
      {
        question: 'Comment fonctionnent les retours ?',
        answer:
          'Vous disposez de 14 jours sous conditions (hors produits d’hygiène ouverts). Voir les conditions générales et la page Retours.',
      },
      {
        question: 'Le paiement est-il sécurisé ?',
        answer:
          'Les paiements en ligne sont chiffrés. La boutique ne stocke pas vos données bancaires complètes.',
      },
      {
        question: 'Les produits sont-ils authentiques ?',
        answer:
          'Nous contrôlons la chaîne d’approvisionnement et la traçabilité des lots. Signalez toute anomalie au service client (e-mail ou WhatsApp).',
      },
    ],
  },
  closing: {
    title: 'Une question avant d’acheter ?',
    ctaPrimary: 'Faire mon diagnostic peau',
    ctaPrimaryHref: '/diagnostic',
    ctaSecondary: 'Voir la boutique',
    ctaSecondaryHref: '/catalogue',
    ctaOnline: 'Faire mon diagnostic peau',
    ctaOnlineHref: '/diagnostic',
  },
  nav: [
    { section: 'accueil', label: 'Accueil' },
    { section: 'marques', label: 'Marques' },
    { section: 'conseil', label: 'Conseil' },
    { section: 'univers', label: 'Univers' },
    { section: 'besoins', label: 'Besoins' },
    { section: 'bestSellers', label: 'Meilleures ventes' },
    { section: 'offres', label: 'Offres' },
  ],
  footer: {
    aboutBlurb: 'Conseil peau et boutique en ligne pour choisir un soin adapté.',
  },
};

function sanitizeLandingShowroom(content: StorefrontHomeLandingContent): StorefrontHomeLandingContent {
  const d = DEFAULT_STOREFRONT_HOME_LANDING;
  const faqItems = content.faq.items.filter(
    (item) => !mentionsShowroom(item.question) && !mentionsShowroom(item.answer),
  );
  return {
    ...content,
    meta: {
      title: mentionsShowroom(content.meta.title) ? d.meta.title : content.meta.title,
      description: mentionsShowroom(content.meta.description) ? d.meta.description : content.meta.description,
    },
    hero: {
      ...content.hero,
      eyebrow: isRetiredHeroEyebrow(content.hero.eyebrow) ? d.hero.eyebrow : content.hero.eyebrow,
      lead:
        mentionsShowroom(content.hero.lead) || isRetiredHeroLead(content.hero.lead)
          ? d.hero.lead
          : content.hero.lead,
      trustLine: isVerboseHeroTrustLine(content.hero.trustLine)
        ? d.hero.trustLine
        : content.hero.trustLine,
      trustChips: sanitizeHeroTrustChips(content.hero.trustChips, d.hero.trustChips ?? []),
      ctaPrimary: isRetiredDiagnosticCta(content.hero.ctaPrimary)
        ? d.hero.ctaPrimary
        : content.hero.ctaPrimary,
      ctaGhost: mentionsShowroom(content.hero.ctaGhost) || isRetiredShopCta(content.hero.ctaGhost)
        ? d.hero.ctaGhost
        : content.hero.ctaGhost,
      ctaGhostHref: landingHrefWithoutShowroom(content.hero.ctaGhostHref, d.hero.ctaGhostHref),
      ctaPrimaryHref: landingHrefWithoutShowroom(content.hero.ctaPrimaryHref, d.hero.ctaPrimaryHref),
    },
    experience: {
      ...content.experience,
      paths: content.experience.paths
        .filter(
          (path) => path.id !== 'showroom' && path.id !== 'tour' && path.icon !== 'showroom' && path.icon !== 'tour',
        )
        .map((path) => ({
          ...path,
          cta: isRetiredDiagnosticCta(path.cta)
            ? d.experience.paths.find((p) => p.id === path.id)?.cta ?? d.hero.ctaPrimary
            : isRetiredShopCta(path.cta)
              ? d.experience.paths.find((p) => p.id === path.id)?.cta ?? d.hero.ctaGhost
              : path.cta,
        })),
    },
    showroom: {
      ...content.showroom,
      bandTitle: content.showroom.bandTitle?.trim() || d.showroom.bandTitle,
      ctaVisit:
        !content.showroom.ctaVisit?.trim() ||
        /^réserver une visite$/i.test(content.showroom.ctaVisit.trim())
          ? d.showroom.ctaVisit
          : content.showroom.ctaVisit.trim(),
    },
    consultation: {
      ...content.consultation,
      lead: mentionsShowroom(content.consultation.lead) ? d.consultation.lead : content.consultation.lead,
      inPersonTitle: mentionsShowroom(content.consultation.inPersonTitle)
        ? d.consultation.inPersonTitle
        : content.consultation.inPersonTitle,
      inPersonCta: isRetiredVisitCopy(content.consultation.inPersonCta)
        ? d.consultation.inPersonCta
        : content.consultation.inPersonCta,
      inPersonHref: landingHrefWithoutShowroom(content.consultation.inPersonHref, d.consultation.inPersonHref),
      onlineCta: isRetiredDiagnosticCta(content.consultation.onlineCta)
        ? d.consultation.onlineCta
        : content.consultation.onlineCta,
    },
    faq: {
      ...content.faq,
      items: faqItems.length > 0 ? faqItems : d.faq.items,
    },
    closing: {
      ...content.closing,
      title: isRetiredVisitCopy(content.closing.title) ? d.closing.title : content.closing.title,
      ctaPrimary: isRetiredVisitCopy(content.closing.ctaPrimary) || isRetiredDiagnosticCta(content.closing.ctaPrimary)
        ? d.closing.ctaPrimary
        : content.closing.ctaPrimary,
      ctaSecondary: isRetiredShopCta(content.closing.ctaSecondary)
        ? d.closing.ctaSecondary
        : content.closing.ctaSecondary,
      ctaPrimaryHref: landingHrefWithoutShowroom(content.closing.ctaPrimaryHref, d.closing.ctaPrimaryHref),
      ctaSecondaryHref: landingHrefWithoutShowroom(content.closing.ctaSecondaryHref, d.closing.ctaSecondaryHref),
    },
    nav: content.nav.filter((item) => item.section !== 'showroom'),
    footer: {
      aboutBlurb: mentionsShowroom(content.footer.aboutBlurb) ? d.footer.aboutBlurb : content.footer.aboutBlurb,
    },
  };
}

export function mergeStorefrontHomeLanding(
  base: StorefrontHomeLandingContent,
  patch: Partial<StorefrontHomeLandingContent>,
): StorefrontHomeLandingContent {
  return sanitizeLandingShowroom({
    ...base,
    ...patch,
    meta: { ...base.meta, ...patch.meta },
    hero: { ...base.hero, ...patch.hero },
    experience: {
      ...base.experience,
      ...patch.experience,
      paths: patch.experience?.paths ?? base.experience.paths,
    },
    showroom: { ...base.showroom, ...patch.showroom },
    consultation: { ...base.consultation, ...patch.consultation },
    brandStrip: { ...base.brandStrip, ...patch.brandStrip },
    univers: {
      ...base.univers,
      ...patch.univers,
      fallbackTiles: patch.univers?.fallbackTiles ?? base.univers.fallbackTiles,
    },
    besoins: { ...base.besoins, ...patch.besoins },
    bestSellers: { ...base.bestSellers, ...patch.bestSellers },
    offers: {
      ...base.offers,
      ...patch.offers,
      fallbackCards: patch.offers?.fallbackCards ?? base.offers.fallbackCards,
    },
    editorial: {
      ...base.editorial,
      ...patch.editorial,
      cards: patch.editorial?.cards ?? base.editorial.cards,
    },
    faq: {
      ...base.faq,
      ...patch.faq,
      items: patch.faq?.items ?? base.faq.items,
    },
    closing: {
      ...base.closing,
      ...patch.closing,
      ctaPrimaryHref: patch.closing?.ctaPrimaryHref || base.closing.ctaPrimaryHref,
      ctaSecondaryHref: patch.closing?.ctaSecondaryHref || base.closing.ctaSecondaryHref,
      ctaOnlineHref: patch.closing?.ctaOnlineHref || base.closing.ctaOnlineHref,
    },
    nav: patch.nav ?? base.nav,
    footer: { ...base.footer, ...patch.footer },
  });
}
